import React, { useState, useEffect, useRef } from 'react';
import { Platform } from '../utils/platform';
import '../styles/payment-status.css';

/**
 * PaymentStatusPage - return URL after YooKassa payment.
 *
 * Extracts payment_id from URL search params (YooKassa may pass it)
 * or from sessionStorage (set before redirect).
 * Polls GET /api/payments/{payment_id}/status every 3s for up to 30s.
 */
const PaymentStatusPage = ({ onNavigate }) => {
  const [status, setStatus] = useState('pending'); // pending | succeeded | canceled | unknown
  const [loading, setLoading] = useState(true);
  const [orderId, setOrderId] = useState(null);
  const pollRef = useRef(null);
  const attemptsRef = useRef(0);
  const MAX_ATTEMPTS = 10; // 10 * 3s = 30s

  useEffect(() => {
    // Try to get payment_id from URL or sessionStorage
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get('payment_id') || sessionStorage.getItem('pending_payment_id');
    const savedOrderId = sessionStorage.getItem('pending_order_id');

    if (savedOrderId) {
      setOrderId(savedOrderId);
    }

    if (!paymentId) {
      setStatus('unknown');
      setLoading(false);
      return;
    }

    const checkStatus = async () => {
      try {
        const { Platform: P } = await import('../utils/platform');
        const token = P.getAuthToken();
        const baseURL = ''; // относительный /api (same-origin); мёртвый window.__ENV__ убран
        const url = baseURL.startsWith('/')
          ? `${baseURL}/api/payments/${paymentId}/status`.replace('/api/api', '/api')
          : `${baseURL}/api/payments/${paymentId}/status`;

        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const resp = await fetch(url, { headers });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();

        if (data.status === 'succeeded' || data.paid === true) {
          setStatus('succeeded');
          setLoading(false);
          clearInterval(pollRef.current);
          sessionStorage.removeItem('pending_payment_id');
          sessionStorage.removeItem('pending_order_id');
          Platform.hapticFeedback('success');
          return;
        }

        if (data.status === 'canceled') {
          setStatus('canceled');
          setLoading(false);
          clearInterval(pollRef.current);
          sessionStorage.removeItem('pending_payment_id');
          sessionStorage.removeItem('pending_order_id');
          Platform.hapticFeedback('error');
          return;
        }

        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          // Still pending after 30s — show pending state
          setStatus('pending');
          setLoading(false);
          clearInterval(pollRef.current);
        }
      } catch (err) {
        console.error('Payment status check failed:', err);
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus('unknown');
          setLoading(false);
          clearInterval(pollRef.current);
        }
      }
    };

    // First check immediately
    checkStatus();
    // Then poll every 3 seconds
    pollRef.current = setInterval(checkStatus, 3000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleGoHome = () => {
    onNavigate('home');
  };

  const handleGoProfile = () => {
    onNavigate('profile');
  };

  return (
    <div className="payment-status-page">
      <div className="payment-status-card">
        {loading ? (
          <>
            <div className="payment-status-icon payment-status-icon--loading">
              <div className="payment-status-spinner" />
            </div>
            <h2 className="payment-status-title">Проверяем оплату...</h2>
            <p className="payment-status-text">
              Обычно это занимает несколько секунд
            </p>
          </>
        ) : status === 'succeeded' ? (
          <>
            <div className="payment-status-icon payment-status-icon--success">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="24" fill="#34C759" opacity="0.12" />
                <path d="M14 24l7 7 13-13" stroke="#34C759" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="payment-status-title">Оплата прошла успешно</h2>
            <p className="payment-status-text">
              {orderId
                ? `Заказ №${orderId} оплачен. Мы свяжемся с вами для уточнения доставки.`
                : 'Ваш заказ оплачен. Мы свяжемся с вами для уточнения доставки.'}
            </p>
          </>
        ) : status === 'canceled' ? (
          <>
            <div className="payment-status-icon payment-status-icon--error">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="24" fill="#FF3B30" opacity="0.12" />
                <path d="M16 16l16 16M32 16L16 32" stroke="#FF3B30" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </div>
            <h2 className="payment-status-title">Оплата не удалась</h2>
            <p className="payment-status-text">
              Платёж был отменён. Вы можете оплатить заказ позже в разделе «Мои заказы».
            </p>
          </>
        ) : status === 'pending' ? (
          <>
            <div className="payment-status-icon payment-status-icon--pending">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="24" fill="#FF9500" opacity="0.12" />
                <circle cx="24" cy="24" r="10" stroke="#FF9500" strokeWidth="2.5" fill="none" />
                <path d="M24 18v7l4 3" stroke="#FF9500" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
            <h2 className="payment-status-title">Ожидаем подтверждение</h2>
            <p className="payment-status-text">
              Платёж обрабатывается. Статус обновится в разделе «Мои заказы».
            </p>
          </>
        ) : (
          <>
            <div className="payment-status-icon payment-status-icon--unknown">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="24" fill="#8E8E93" opacity="0.12" />
                <text x="24" y="30" textAnchor="middle" fill="#8E8E93" fontSize="22" fontWeight="600">?</text>
              </svg>
            </div>
            <h2 className="payment-status-title">Статус неизвестен</h2>
            <p className="payment-status-text">
              Не удалось проверить статус платежа. Проверьте в разделе «Мои заказы».
            </p>
          </>
        )}

        <div className="payment-status-actions">
          <button className="payment-status-btn payment-status-btn--primary" onClick={handleGoHome}>
            На главную
          </button>
          <button className="payment-status-btn payment-status-btn--secondary" onClick={handleGoProfile}>
            Мои заказы
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatusPage;
